import type { TransactionEvent } from "./types";

/**
 * Token-bucket + weighted reservoir buffer.
 *
 * - At low TPS, events drain as they arrive (bucket stays full).
 * - At high TPS, reservoir caps concurrent arcs — the display stays calm
 *   while corridor intensity tells the real story.
 * - At zero TPS, nothing drains; quiet mode takes over rendering.
 */
export class FlowBuffer {
  private bucket: number;
  private reservoir: TransactionEvent[] = [];
  private lastTick = performance.now();

  constructor(
    private readonly refillPerSec = 8,
    private readonly bucketCap = 24,
    private readonly reservoirSize = 200,
  ) {
    this.bucket = bucketCap;
  }

  push(ev: TransactionEvent) {
    if (this.reservoir.length < this.reservoirSize) {
      this.reservoir.push(ev);
      return;
    }
    // Weighted replacement — larger amounts survive longer
    const victimIdx = Math.floor(Math.random() * this.reservoir.length);
    const victim = this.reservoir[victimIdx];
    if (Math.log10(ev.amountUsd + 1) > Math.log10(victim.amountUsd + 1) * Math.random()) {
      this.reservoir[victimIdx] = ev;
    }
  }

  /** Called from the render loop. Returns events to dispatch this frame. */
  drain(now: number): TransactionEvent[] {
    const dt = (now - this.lastTick) / 1000;
    this.lastTick = now;
    this.bucket = Math.min(this.bucketCap, this.bucket + dt * this.refillPerSec);

    const out: TransactionEvent[] = [];
    while (this.bucket >= 1 && this.reservoir.length) {
      out.push(this.pickWeighted());
      this.bucket -= 1;
    }
    return out;
  }

  private pickWeighted(): TransactionEvent {
    // Prefer larger amounts — brighter events get screen time.
    let total = 0;
    for (const e of this.reservoir) total += Math.log10(e.amountUsd + 1);
    let r = Math.random() * total;
    for (let i = 0; i < this.reservoir.length; i++) {
      r -= Math.log10(this.reservoir[i].amountUsd + 1);
      if (r <= 0) return this.reservoir.splice(i, 1)[0];
    }
    return this.reservoir.shift()!;
  }

  get pending() {
    return this.reservoir.length;
  }
}
